import React from 'react'
import { PaymentPlan, PendingAction, PricingPlan, PricingPlanAction } from '../types'
import { formatRenewal, getYearlyPrice, isTwoYearPaymentPlan } from '../pricingPlanFormSchema'
import { formatISOString } from 'utils'
import { DateFormats } from 'utils/dates'
import classNames from 'classnames'
import Badge from 'uiComponents/badge'
import { KebabMenu, KebabMenuOption } from 'uiComponents/menus/kebabMenu'
import { PricingPlanSettings } from './pricingPlanSettings'
import { getPricingPlanStatusConfig, getKebabMenuLabels } from './pricingPlanConfig'
import { useTranslation } from 'translations'
import './styles.scss'

interface PricingPlanElementProps {
    pricingPlan: PricingPlan
    setPendingAction?: (pendingAction: PendingAction) => void
    readOnly?: boolean
}

const buildKebabActions = (
    actions: PricingPlanAction[],
    onAction: (action: PricingPlanAction) => void,
): KebabMenuOption[] => {
    const kebabMenuLabels = getKebabMenuLabels()
    return actions.map((action) => ({
        ...kebabMenuLabels[action],
        onClick: () => onAction(action),
    }))
}

interface PaymentPlanRowProps {
    paymentPlan: PaymentPlan | undefined
    badgeLabel?: string
    badgeVariant?: string
    showMemberCount: boolean
    actions: KebabMenuOption[]
    readOnly?: boolean
}

const PaymentPlanRow = ({ paymentPlan, badgeLabel, badgeVariant, showMemberCount, actions, readOnly }: PaymentPlanRowProps) => {
    const { t } = useTranslation()
    return (
        <div className={classNames('pricing-plan', paymentPlan?.status)}>
            <div className="pricing-plan-info">
                <div className="member-count">
                    {badgeVariant && badgeLabel && <Badge label={badgeLabel} variant={badgeVariant as any} />}
                    {showMemberCount &&
                        t('products.pricing.pricingPlans.memberCount', { memberCount: paymentPlan?.memberCount })}
                </div>
            </div>
            <PricingPlanSettings
                renewal={formatRenewal(paymentPlan?.automaticRenewal, paymentPlan)}
                contract={
                    paymentPlan && isTwoYearPaymentPlan(paymentPlan)
                        ? t('products.pricing.pricingPlans.twoYears')
                        : undefined
                }
                annualTotal={paymentPlan?.paymentInterval === 'P1Y' ? getYearlyPrice(paymentPlan) : undefined}
                upfrontTotal={paymentPlan?.paymentInterval === 'P2Y' ? paymentPlan?.totalPrice : undefined}
                monthlyTotal={paymentPlan?.paymentInterval === 'P1M' ? paymentPlan?.totalPrice : undefined}
                downPayment={paymentPlan?.initialPayment}
                monthly={paymentPlan?.installment}
            />
            {!readOnly && actions.length > 0 && (
                <div className="pricing-plan-actions">
                    <KebabMenu listAnchor="right" options={actions} />
                </div>
            )}
        </div>
    )
}

export const PricingPlanElement = ({ pricingPlan, setPendingAction, readOnly }: PricingPlanElementProps) => {
    const { t } = useTranslation()
    const { date, status, paymentPlans } = pricingPlan
    const totalMembers = paymentPlans.reduce((acc, plan) => acc + plan.memberCount, 0)
    const pricingPlanStatusConfig = getPricingPlanStatusConfig()
    const config = pricingPlanStatusConfig[status]

    const onAction = (targetPlan: PricingPlan) => (action: PricingPlanAction) =>
        setPendingAction?.({ action, pricingPlan: targetPlan })

    const withPaymentPlans = (plans: PaymentPlan[]): PricingPlan => ({ ...pricingPlan, paymentPlans: plans })

    if (status === 'conditionally_migrating') {
        const sortedPaymentPlans = [...paymentPlans].sort((a, b) => {
            if (a.status === 'migrating') return -1
            if (b.status === 'migrating') return 1
            return 0
        })

        return (
            <li className="grouped-pricing-plan-container">
                <div className="grouped-pricing-plan-date">
                    <span className="date">{formatISOString(date, DateFormats.SHORT_DATE)}</span>
                    &nbsp;&nbsp;
                    <span className="member-count">
                        {t('products.pricing.pricingPlans.memberCount', { memberCount: totalMembers })}
                    </span>
                </div>
                <div className="grouped-pricing-plan">
                    {sortedPaymentPlans.map((paymentPlan, index) => {
                        const planStatus = paymentPlan.status!
                        const planConfig = pricingPlanStatusConfig[planStatus]
                        const availableActions = planStatus === 'open'
                            ? planConfig.availableActions.filter((a) => a !== 'remove')
                            : planConfig.availableActions
                        const actions = buildKebabActions(
                            availableActions,
                            onAction({ ...withPaymentPlans([paymentPlan]), status: planStatus }),
                        )
                        return (
                            <React.Fragment key={paymentPlan.planUuid}>
                                {index > 0 && <div className="payment-plan-separator" />}
                                <PaymentPlanRow
                                    paymentPlan={paymentPlan}
                                    badgeLabel={planConfig.badgeLabel}
                                    badgeVariant={planConfig.badgeVariant}
                                    showMemberCount={config.showMemberCount}
                                    actions={actions}
                                    readOnly={readOnly}
                                />
                            </React.Fragment>
                        )
                    })}
                </div>
            </li>
        )
    }

    const monthlyPaymentPlan = paymentPlans.find((plan) => plan.paymentInterval === 'P1M')
    const annualPaymentPlan = paymentPlans.find((plan) => plan.paymentInterval === 'P1Y')
    const upfrontPaymentPlan = paymentPlans.find((plan) => plan.paymentInterval === 'P2Y')
    const renewalSourcePlan =
        [annualPaymentPlan, upfrontPaymentPlan, monthlyPaymentPlan].find((plan) => plan?.automaticRenewal) ??
        annualPaymentPlan ??
        upfrontPaymentPlan ??
        monthlyPaymentPlan
    const renewal = formatRenewal(renewalSourcePlan?.automaticRenewal, renewalSourcePlan)
    const isTwoYearContract = paymentPlans.some(isTwoYearPaymentPlan)
    const actions = buildKebabActions(config.availableActions, onAction(pricingPlan))

    return (
        <li className={classNames('pricing-plan', status)}>
            <div className="pricing-plan-info">
                {config.showDate && <span className="date">{formatISOString(date, DateFormats.SHORT_DATE)}</span>}
                <div className="member-count">
                    {config.badgeVariant && config.badgeLabel && <Badge label={config.badgeLabel} variant={config.badgeVariant} />}
                    {config.showMemberCount &&
                        t('products.pricing.pricingPlans.memberCount', { memberCount: totalMembers })}
                </div>
            </div>
            <PricingPlanSettings
                renewal={renewal}
                contract={isTwoYearContract ? t('products.pricing.pricingPlans.twoYears') : undefined}
                annualTotal={annualPaymentPlan ? getYearlyPrice(annualPaymentPlan) : undefined}
                upfrontTotal={upfrontPaymentPlan?.totalPrice}
                monthlyTotal={monthlyPaymentPlan?.totalPrice}
                downPayment={monthlyPaymentPlan?.initialPayment}
                monthly={monthlyPaymentPlan?.installment}
            />
            {!readOnly && (
                <div className="pricing-plan-actions">
                    {actions.length > 0 && <KebabMenu listAnchor="right" options={actions} />}
                </div>
            )}
        </li>
    )
}
